import { useRef, useState } from "react";
import { WarningSVG } from "../../../../../assets/icon/svg";
import PolicyTATRows from "./policy-tat-rows";
import { usePolicyData } from "../../../../../hooks/populate-data/use-policy-data";
import PolicyTabHeader from "./policy-tab-header";
import useStickyTableHeader from "../../../../../hooks/effect/use-sticky-table-header";
import useTableHorizontalScroll from "../../../../../hooks/effect/use-table-horizontal-scroll";
import useTableDimensions from "../../../../../hooks/effect/use-table-dimensions";
import { usePolicyDataState } from "../../../../../hooks/policy/use-policy-data-state";

const thead = [
    { id: 1, name: "Transaction Category" },
    { id: 2, name: "TAT (WD)" },
    { id: 3, name: "Reckoning Date" },
    { id: 4, name: "Mode of Communication" },
    { id: 5, name: "Responsible" },
    { id: 6, name: "Contact Details" },
];

export const columnWidths = [
    {
        min: "min-w-[150px] sm:min-w-[180px] lg:min-w-[220px] xl:min-w-[240px]",
        max: "max-w-[180px] sm:max-w-[220px] lg:max-w-[260px] xl:max-w-[280px]",
    },
    {
        min: "min-w-[70px] sm:min-w-[80px] lg:min-w-[90px] xl:min-w-[100px]",
        max: "max-w-[120px] sm:max-w-[130px] lg:max-w-[140px] xl:max-w-[150px]",
    },
    {
        min: "min-w-[150px] sm:min-w-[170px] lg:min-w-[190px] xl:min-w-[210px]",
        max: "max-w-[190px] sm:max-w-[210px] lg:max-w-[230px] xl:max-w-[250px]",
    },
    {
        min: "min-w-[170px] sm:min-w-[190px] lg:min-w-[210px] xl:min-w-[230px]",
        max: "max-w-[200px] sm:max-w-[220px] lg:max-w-[240px] xl:max-w-[270px]",
    },
    {
        min: "min-w-[150px] sm:min-w-[170px] lg:min-w-[190px] xl:min-w-[210px]",
        max: "max-w-[180px] sm:max-w-[210px] lg:max-w-[230px] xl:max-w-[250px]",
    },
    {
        min: "min-w-[170px] sm:min-w-[190px] lg:min-w-[210px] xl:min-w-[230px]",
        max: "max-w-[200px] sm:max-w-[220px] lg:max-w-[250px] xl:max-w-[280px]",
    },
];

export default function PolicyTurnAroundTime() {

    const { tatItemsApi } = usePolicyData();

    const [isHeaderSticky, setIsHeaderSticky] = useState(false);
    const [headerWidth, setHeaderWidth] = useState(0);
    const [headerLeft, setHeaderLeft] = useState(0);
    const [stickyTop, setStickyTop] = useState(0);
    const [headerHeight, setHeaderHeight] = useState(0);
    const [firstColumnWidth, setFirstColumnWidth] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);

    const {
        policyIsEditingTAT,
        policyTATItems,
    } = usePolicyDataState()

    const tableWrapperRef = useRef<HTMLDivElement | null>(null);
    const tableRef = useRef<HTMLTableElement | null>(null);
    const headerPlaceholderRef = useRef<HTMLTableSectionElement | null>(null);

    const displayTATItems = policyIsEditingTAT ? policyTATItems ?? [] : tatItemsApi ?? [];

    /* * Sticky header detection. */
    useStickyTableHeader({
        tableWrapperRef, 
        setIsHeaderSticky, 
        setHeaderWidth, 
        setHeaderLeft, 
        setStickyTop,
    })

    /* Track horizontal scrolling of the table. */
    useTableHorizontalScroll({
        tableWrapperRef,
        setScrollLeft,
    })

    /* Measure table/header dimensions. */
    useTableDimensions({
        tableWrapperRef, 
        tableRef, 
        setHeaderWidth, 
        setHeaderLeft, 
        setHeaderHeight, 
        setFirstColumnWidth,
    })

    return (
        <section className="overflow-hidden rounded border border-gray-200 bg-white">
            <PolicyTabHeader
                title="Turn-Around Time (TAT)"
                subTitle="Service Level Agreement (SLA). Review and update the TAT,
                reckoning date, and mode of communication for each
                applicable transaction."
            />
            <div className="space-y-4 px-4 py-3 lg:space-y-5">
                {policyIsEditingTAT && (
                    <div className="mb-4 flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5">
                        <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-100">
                            <WarningSVG className="h-3.5 w-3.5 text-amber-600" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-xs font-semibold text-amber-900">
                                Editing Turn-Around Time
                            </p>
                            <p className="mt-0.5 text-[11px] leading-4 text-amber-800 sm:text-xs">
                                You can modify{" "}
                                <span className="font-medium">
                                    TAT, Reckoning Date, and Mode of Communication
                                </span>
                                .{" "}
                                <span className="font-medium">
                                    Responsible and Contact Details
                                </span>{" "}
                                are read-only.
                            </p>
                        </div>
                    </div>
                )}
                {/* Table */}
                <div ref={tableWrapperRef} className="relative w-full overflow-x-auto rounded border border-gray-300">
                    <table ref={tableRef} className="w-full min-w-[900px] border-separate border-spacing-0 text-sm">
                        <thead ref={headerPlaceholderRef} className={isHeaderSticky ? "invisible" : "visible"} >
                            <tr className="bg-[#eef6fc]">
                                {thead.map((data, index) => {
                                    const width = columnWidths[index];
                                    return (
                                        <th
                                            key={data.id}
                                            className={`sticky top-0 border-b border-[#c7e0f3] bg-[#eef6fc] px-3 py-3 text-left text-xs font-semibold text-gray-800 sm:px-4 
                                                ${width?.min ?? "" } 
                                                ${width?.max ?? ""} 
                                                ${index === 0 ? "left-0 z-30 after:absolute after:inset-y-0 after:right-0 after:w-px after:bg-[#c7e0f3]"
                                                    : "z-20 border-r text-center"}
                                            `}
                                        >
                                            {data.name}
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {/* QUOTATION */}
                            <PolicyTATRows
                                colSpan={6}
                                title="QUOTATION"
                                items={displayTATItems.filter((item) => item.category === "QUOTATION")}
                                isEditingTAT={policyIsEditingTAT}
                            />

                            {/* NEGOTIATION */}
                            <PolicyTATRows
                                colSpan={6}
                                title="NEGOTIATION"
                                items={displayTATItems.filter((item) => item.category === "NEGOTIATION")}
                                isEditingTAT={policyIsEditingTAT}
                            />

                            {/* NEW BUSINESS */}
                            <PolicyTATRows
                                colSpan={6}
                                title="NEW BUSINESS"
                                items={displayTATItems.filter((item) => item.category === "NEW BUSINESS")}
                                isEditingTAT={policyIsEditingTAT}
                            />

                            {/* UNDERWRITING */}
                            <PolicyTATRows
                                colSpan={6}
                                title="UNDERWRITING"
                                items={displayTATItems.filter((item) => item.category === "UNDERWRITING")}
                                isEditingTAT={policyIsEditingTAT}
                            />

                            {/* ISSUANCE */}
                            <PolicyTATRows
                                colSpan={6}
                                title="ISSUANCE"
                                items={displayTATItems.filter((item) => item.category === "ISSUANCE")}
                                isEditingTAT={policyIsEditingTAT}
                            />

                            {/* CANCELLATION */}
                            <PolicyTATRows
                                colSpan={6}
                                title="COLLECTION"
                                items={displayTATItems.filter(
                                    (item) =>
                                        item.category === "COLLECTION"
                                )}
                                isEditingTAT={policyIsEditingTAT}
                            />

                            {/* BILLING */}
                            <PolicyTATRows
                                colSpan={6}
                                title="BILLING"
                                items={displayTATItems.filter(
                                    (item) =>
                                        item.category === "BILLING"
                                )}
                                isEditingTAT={policyIsEditingTAT}
                            />

                            {/* COLLECTION */}
                            <PolicyTATRows
                                colSpan={6}
                                title="COLLECTION"
                                items={displayTATItems.filter(
                                    (item) =>
                                        item.category === "COLLECTION"
                                )}
                                isEditingTAT={policyIsEditingTAT}
                            />

                            {/* CLAIMS */}
                            <PolicyTATRows
                                colSpan={6}
                                title="CLAIMS"
                                items={displayTATItems.filter(
                                    (item) =>
                                        item.category === "CLAIMS"
                                )}
                                isEditingTAT={policyIsEditingTAT}
                            />

                            {/* REPORTS */}
                            <PolicyTATRows
                                colSpan={6}
                                title="REPORTS"
                                items={displayTATItems.filter(
                                    (item) =>
                                        item.category === "REPORTS"
                                )}
                                isEditingTAT={policyIsEditingTAT}
                            />

                            {/* COMMUNICATION */}
                            <PolicyTATRows
                                colSpan={6}
                                title="COMMUNICATION"
                                items={displayTATItems.filter(
                                    (item) =>
                                        item.category === "COMMUNICATION"
                                )}
                                isEditingTAT={policyIsEditingTAT}
                            />
                        </tbody>
                    </table>

                    {/* STICKY HEADER */}
                    {isHeaderSticky && (
                        <div
                            className="pointer-events-none fixed z-40 overflow-hidden border-b border-[#c7e0f3] bg-[#eef6fc]"
                            style={{ top: stickyTop, left: headerLeft, width: headerWidth, height: headerHeight, }}
                        >
                            {/* FIXED FIRST COLUMN */}
                            <div
                                className="absolute left-0 top-0 z-50 flex items-center border-r border-[#c7e0f3] bg-[#eef6fc] px-3 py-3 text-left text-xs font-semibold text-gray-800 sm:px-4"
                                style={{ width: firstColumnWidth, height: headerHeight, }}
                            >
                                <span className="whitespace-nowrap">
                                    {thead[0].name}
                                </span>

                                <span className="absolute inset-y-0 right-0 w-px bg-[#c7e0f3]" />
                            </div>

                            {/* MOVING COLUMNS */}
                            <div
                                className="absolute right-0 top-0 overflow-hidden"
                                style={{
                                    left: firstColumnWidth,
                                    height: headerHeight,
                                }}
                            >
                                <table
                                    className="border-separate border-spacing-0 text-sm"
                                    style={{
                                        minWidth: "900px",
                                        transform: `translateX(-${scrollLeft}px)`,
                                    }}
                                >
                                    <thead>
                                        <tr className="bg-[#eef6fc]">
                                            {thead.slice(1).map((data, index) => {
                                                    const actualIndex = index + 1;
                                                    const width = columnWidths[actualIndex];
                                                    return (
                                                        <th
                                                            key={data.id}
                                                            className={`border-b border-r border-[#c7e0f3] bg-[#eef6fc] px-3 py-3 text-center text-xs font-semibold text-gray-800 sm:px-4 
                                                                ${width?.min ?? ""} ${width?.max ?? ""}`}
                                                        >
                                                            {data.name}
                                                        </th>
                                                    );
                                                })}
                                        </tr>
                                    </thead>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                {/* Information */}
                <div className="mt-4 rounded border border-[#c7e0f3] bg-[#eef6fc] p-3">
                    <div className="flex gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#186cb2]" fill="none" viewBox="0 0 24 24" stroke="currentColor" >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 22a10 10 0 100-20 10 10 0 000-20z" />
                        </svg>
                        <div>
                            <p className="text-xs font-semibold text-[#186cb2]">
                                Service Level Agreement
                            </p>
                            <p className="mt-1 text-[10px] leading-4 text-[#145a96] sm:text-xs">
                                Update only the TAT, reckoning date, and mode
                                of communication. Responsible parties and
                                contact details are predefined and cannot be
                                modified.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}